import {
  SchoolSportSeason,
  SportSeason
} from '@maxpreps/apiserviceclients/apiModels';
import dayjs from 'dayjs';
import GenderName from '../../constants/genderName';
import Season from '../../constants/season';
import Sport from '../../constants/sportName';
import TeamLevel from '../../constants/teamLevel';
import getSportDefinition from './getSportDefinition';

export const getLevels = (sportSeasons: SportSeason[]): string[] => {
  if (!sportSeasons) return [];
  const levels = new Set();
  sportSeasons.forEach(ss => {
    levels.add(ss.level);
  });
  return Array.from(levels) as string[];
};

export const filterByLevel = (sportSeasons, level) => {
  if (!sportSeasons || !level) return [];
  const seasons: any[] = [];
  sportSeasons.forEach(ss => {
    if (ss.level === level) {
      seasons.push(ss);
    }
  });
  return seasons;
};

// TODO: Type the return
export const separateSportSeasonsByGender = <T extends SportSeason>(
  sportSeasons: T[]
) => {
  const genders = {};
  sportSeasons.forEach(sportSeason => {
    const tempGenders: any[] = [];
    // add co-ed sports to boys and girls
    // this will add a gender when a school might be all boys or girls >_<
    // TODO need to refine
    // hole in logic - don't show "gender a" list for all-"gender b" schools
    if (sportSeason.gender === GenderName.CoEd) {
      tempGenders.push(GenderName.Boys, GenderName.Girls);
    } else {
      tempGenders.push(sportSeason.gender);
    }

    tempGenders.forEach(tempGender => {
      let gender = genders[tempGender];

      if (!gender) {
        gender = {};
        genders[tempGender] = gender;
      }

      let sport = gender[sportSeason.sport];
      if (!sport) {
        sport = [];
        gender[sportSeason.sport] = sport;
      }

      if (
        sport.find(
          tempSportSeason => tempSportSeason.level === sportSeason.level
        ) == null
      ) {
        sport.push(sportSeason);
      }
    });
  });

  return genders;
  //  as Record<
  //   keyof GenderName,
  //   Record<keyof Sport, SportSeason[]>
  // >;
};

export const formatYearForSeason = (year: string, season: Season | string) => {
  if (year?.length !== 5) return year;
  const [start, end] = year.split('-');
  if (season === Season.Fall) return `20${start}`;
  if (season === Season.Winter) return `20${start}-${end}`;
  if (season === Season.Spring) return `20${end}`;
  return year;
};

export const formatGenderSport = (gender: string, sport: string) => {
  const { isSingleGender } = getSportDefinition(sport.toLowerCase());
  return isSingleGender ? sport : `${gender?.charAt(0) || ''}. ${sport}`;
};

export const formatTeamLevel = (teamLevel: TeamLevel): string => {
  if (teamLevel === TeamLevel.JV) return 'JV';
  return teamLevel.charAt(0);
};

// Filter all but the most recent sport season for each gender + sport + season + teamlevel

export const filterMostRecentSportSeasons = <T extends SportSeason>(
  sportSeasons: T[],
  includeUnpublished: boolean
): T[] => {
  if (!sportSeasons) {
    return [];
  }

  const filteredSportSeasons: any[] = [];
  const keys = {};
  for (let i = 0; i < sportSeasons.length; i++) {
    const ss = sportSeasons[i];
    const key = `${ss.gender},${ss.sport},${ss.season},${ss.level}`;

    if ((includeUnpublished || ss.isPublished) && !keys[key]) {
      filteredSportSeasons.push(ss);
      keys[key] = true;
    }
  }

  return filteredSportSeasons;
};

export const filterByGenderSport = <T extends SportSeason>(
  ssid: string,
  data: T[],
  options?: {
    matchTeamLevel?: boolean;
    matchSeason?: boolean;
    includeUnpublished?: boolean;
  }
): T[] => {
  if (!data) {
    return [];
  }

  const anchor = data.find(sportSeason => sportSeason.sportSeasonId === ssid);

  if (!anchor) return [];

  return data.filter(
    sportSeason =>
      sportSeason.sport === anchor.sport &&
      sportSeason.gender === anchor.gender &&
      ((options?.includeUnpublished ?? false) || sportSeason.isPublished) &&
      (!(options?.matchTeamLevel ?? false) ||
        sportSeason.level == anchor.level) &&
      (!(options?.matchSeason ?? false) || sportSeason.season == anchor.season)
  );
};

export const filterUnqiueSportSeasons = <T extends SportSeason>(
  data: T[]
): T[] =>
  data?.filter(
    (sportSeason, i, arr) =>
      arr.findIndex(t => t.sportSeasonId === sportSeason.sportSeasonId) === i
  );

// Filtering out - name, genderId, sportId, levelId, teamLevelId, seasonId, isActive, isFullyCovered, isPublished, isNational, formattedNameFanPageDefault, formattedNameAdminDefault
// by adding any of these back it will start to increase the size of the teams app initial bundle
export const filterOutNonImportantProperties = (data: SchoolSportSeason[]) =>
  data?.map(sportSeason => ({
    schoolId: sportSeason.schoolId,
    canonicalUrl: sportSeason.canonicalUrl,
    sportSeasonId: sportSeason.sportSeasonId,
    allSeasonId: sportSeason.allSeasonId,
    gender: sportSeason.gender,
    sport: sportSeason.sport,
    level: sportSeason.level,
    season: sportSeason.season,
    year: sportSeason.year,
    teamLevel: sportSeason.teamLevel,
    isPublished: sportSeason.isPublished
  }));

export const getPreferredSeason = () => {
  const now = dayjs();
  const monthOfYear = now.month();
  const dayOfMonth = now.date();

  if (monthOfYear < 2 && dayOfMonth < 15) {
    return Season.Winter;
  }
  if (monthOfYear < 7) {
    return Season.Spring;
  }
  if (monthOfYear < 11 && dayOfMonth < 15) {
    return Season.Fall;
  }

  return Season.Winter;
};

export const getEnumsFromGenderSportId = (
  genderSportId: string
): [GenderName | undefined, Sport | undefined] => {
  const [gender, sport] = genderSportId.split(',');
  const genderName = Object.values(GenderName).find(
    gendername => gender.toLowerCase() === gendername.toLowerCase()
  );
  const sportIndex = Object.keys(Sport).find(
    sportname => sport.toLowerCase() === sportname.toLowerCase()
  );
  const sportName = sportIndex ? Sport[sportIndex] : undefined;
  return [genderName, sportName];
};
